// src/components/pricingection.jsx

import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  Alert,
  useTheme,
  useMediaQuery,
  Button,
  CircularProgress,
  IconButton,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useMotionValueEvent,
} from "framer-motion";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { fadeUp, containerStagger } from "../utils/animations";

const MotionBox = motion(Box);

const BROWN = {
  ink: "#231B16",
  inkLight: "#352A23",
  muted: "#75675E",
  mutedLight: "#A09289",
  accent: "#A06E50",
  accentBright: "#C68B59",
  paper: "#F7F4EF",
  surface: "#FCFAF6",
  line: "#DCD4CC",
  lineDark: "rgba(255, 255, 255, 0.16)",
  soft: "#EEE8E1",
};

// Fallback high-impact services if Firestore query is empty or loading
const FALLBACK_SERVICES = [
  {
    id: "web-apps",
    title: "High-Performance Web Platforms",
    subtitle:
      "Turnkey full-stack web applications engineered with fluid kinetic motion, sub-second latency, and enterprise-grade scalability.",
    category: "Full-Stack Development",
    imageUrl: "/assets/projects/1.jpeg",
    tags: ["React & Next.js", "Cloud APIs", "Instant UX", "SEO Architecture"],
  },
  {
    id: "ui-ux-design",
    title: "Editorial UI & Digital Systems",
    subtitle:
      "Art-directed digital interfaces that elevate brand authority with bespoke design tokens, fluid layouts, and micro-interactions.",
    category: "Product & UI Design",
    imageUrl: "/assets/projects/2.jpeg",
    tags: ["Design Systems", "Prototyping", "Design Engineering", "Figma to Code"],
  },
  {
    id: "mobile-apps",
    title: "Cross-Platform Mobile Suites",
    subtitle:
      "Native-feel applications crafted for iOS and Android with realtime synchronization, offline caching, and biometric authentication.",
    category: "Mobile Engineering",
    imageUrl: "/assets/projects/3.jpeg",
    tags: ["iOS & Android", "Offline Sync", "Push Pipelines", "Fluid Gestures"],
  },
  {
    id: "cloud-backend",
    title: "Cloud Infrastructure & APIs",
    subtitle:
      "Resilient microservices, Firestore & SQL integrations, and serverless pipelines tuned for 99.99% uptime and zero friction.",
    category: "Cloud Architecture",
    imageUrl: "/assets/projects/4.jpeg",
    tags: ["Serverless", "Realtime DB", "Security Rules", "High Concurrency"],
  },
  {
    id: "brand-strategy",
    title: "Digital Strategy & Launch Passes",
    subtitle:
      "End-to-end launch roadmaps, conversion-focused landing experiences, and technical execution for ambitious digital ventures.",
    category: "Growth & Strategy",
    imageUrl: "/assets/projects/5.jpeg",
    tags: ["Launch Systems", "Conversion Tuning", "Performance Audits"],
  },
];

const ServiceSection = () => {
  const [services, setServices] = useState([]);
  const [activeServiceIndex, setActiveServiceIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scrollRange, setScrollRange] = useState(0);

  const sectionRef = useRef(null);
  const trackRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  // Firestore real-time listener
  useEffect(() => {
    const colRef = collection(db, "services");
    const q = query(colRef, orderBy("priority", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const servicesData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setServices(servicesData);
        setError(null);
        setLoading(false);
      },
      (err) => {
        console.error("Real-time services fetch error:", err);
        setError("Failed to load live services; showing studio selections.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Compute final services list (use fetched or fallbacks)
  const displayedServices = useMemo(() => {
    if (services && services.length > 0) {
      return services.slice(0, 8);
    }
    return FALLBACK_SERVICES;
  }, [services]);

  const totalCards = displayedServices.length;

  // Track vertical scroll over the section container
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  // Calculate horizontal travel distance dynamically based on track width vs viewport
  useEffect(() => {
    const measure = () => {
      if (trackRef.current) {
        const trackWidth = trackRef.current.scrollWidth;
        const viewportWidth = window.innerWidth;
        // Leave comfortable right clearance for the final card
        const clearance = Math.max(80, viewportWidth * 0.1);
        const distance = Math.max(0, trackWidth - viewportWidth + clearance);
        setScrollRange(distance);
      }
    };

    measure();
    const timer = setTimeout(measure, 350);
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", measure);
    };
  }, [displayedServices.length, isMobile]);

  // Spring physics for buttery, cinema-grade horizontal sliding
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 75,
    damping: 24,
    mass: 0.8,
    restDelta: 0.001,
  });

  // Transform vertical progress (0 -> 1) into horizontal X translation
  const x = useTransform(smoothProgress, [0, 1], [0, -scrollRange]);

  // Update active card index based on scroll position
  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    if (totalCards === 0) return;
    const computedIndex = Math.min(
      totalCards - 1,
      Math.max(0, Math.floor(latest * totalCards))
    );
    setActiveServiceIndex(computedIndex);
  });

  // Manual programmatic navigation (Next / Prev)
  const handleScrollToCard = (direction) => {
    if (!sectionRef.current) return;
    const nextIdx =
      direction === "next"
        ? Math.min(totalCards - 1, activeServiceIndex + 1)
        : Math.max(0, activeServiceIndex - 1);

    const targetFraction = nextIdx / Math.max(1, totalCards - 1);
    const rect = sectionRef.current.getBoundingClientRect();
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const totalTravel = sectionRef.current.offsetHeight - window.innerHeight;
    const targetY = scrollTop + rect.top + totalTravel * targetFraction;

    window.scrollTo({
      top: targetY,
      behavior: "smooth",
    });
  };

  return (
    <Box
      component="section"
      id="services-section"
      sx={{
        position: "relative",
        backgroundColor: 'transparent',
        color: BROWN.ink,
        overflow: "visible",
      }}
    >
      {/* Editorial Header Section before pinning */}
      <Box
        sx={{
          maxWidth: 1600,
          mx: "auto",
          pt: { xs: 8, md: 12 },
          pb: { xs: 4, md: 6 },
          px: { xs: 3, md: 6 },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.3fr .7fr" },
            gap: { xs: 4, md: 8 },
            alignItems: "end",
          }}
        >
          <MotionBox
            variants={containerStagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <MotionBox variants={fadeUp}>

              <Typography
                component="h2"
                sx={{
                  fontSize: { xs: "2.8rem", sm: "4rem", md: "5.4rem" },
                  lineHeight: { xs: 1, md: 0.92 },
                  letterSpacing: "-.06em",
                  fontWeight: 800,
                  color: BROWN.ink,
                }}
              >
                Ideas turned into
                <br />
                <Box
                  component="span"
                  sx={{
                    color: BROWN.accent,
                    background: `linear-gradient(135deg, ${BROWN.accent} 0%, ${BROWN.accentBright} 100%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  extraordinary things.
                </Box>
              </Typography>
            </MotionBox>
          </MotionBox>

          <MotionBox
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            sx={{
              borderLeft: { xs: "none", md: `1px solid ${BROWN.line}` },
              pl: { xs: 0, md: 5 },
              pb: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "0.95rem", md: "1.05rem" },
                lineHeight: 1.75,
                color: BROWN.muted,
                maxWidth: 440,
                mb: 3,
              }}
            >
              From the first wireframe to the final launch, we engineer digital
              experiences that are clear, uncompromisingly fast, and made to last.
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Button
                onClick={() => navigate("/pricing")}
                endIcon={<NorthEastIcon sx={{ fontSize: "1rem" }} />}
                sx={{
                  px: 0,
                  py: 0.5,
                  color: BROWN.ink,
                  fontSize: ".84rem",
                  fontWeight: 750,
                  textTransform: "none",
                  borderRadius: 0,
                  borderBottom: `1.5px solid ${BROWN.ink}`,
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: BROWN.accent,
                    borderColor: BROWN.accent,
                  },
                }}
              >
                Explore complete service catalog
              </Button>
            </Box>
          </MotionBox>
        </Box>
      </Box>

      {/* Realtime Status Alerts */}
      {loading && (
        <Box
          sx={{
            py: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <CircularProgress size={30} sx={{ color: BROWN.accent }} />
          <Typography
            sx={{ color: BROWN.muted, fontSize: ".85rem", letterSpacing: ".04em" }}
          >
            Loading studio capabilities...
          </Typography>
        </Box>
      )}

      {error && !loading && (
        <Box sx={{ px: { xs: 2, md: 6 }, mb: 3 }}>
          <Alert
            severity="info"
            sx={{
              maxWidth: 700,
              mx: "auto",
              borderRadius: "12px",
              backgroundColor: BROWN.surface,
              border: `1px solid ${BROWN.line}`,
              color: BROWN.ink,
            }}
          >
            {error}
          </Alert>
        </Box>
      )}

      {/* ========================================================================= */}
      {/* 🚀 THE IMMERSIVE X-AXIS STICKY SCROLL SECTION                            */}
      {/* ========================================================================= */}
      <Box
        ref={sectionRef}
        sx={{
          position: "relative",
          // The height determines how long the user scrolls through the horizontal showcase
          height: {
            xs: `calc(100vh + ${(totalCards + 1) * 45}vh)`,
            md: `calc(100vh + ${(totalCards + 1) * 90}vh)`,
          },
          backgroundColor: 'transparent',
        }}
      >
        {/* Sticky Window Container (pins at top: 0, takes full viewport) */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            height: "90vh",
            width: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            py: { xs: 3, md: 4 },
          }}
        >

          {/* 🌟 Horizontal Motion Track (Transforms on X-axis) */}
          <Box
            sx={{
              position: "relative",
              width: "100%",
              overflow: "visible",
              my: "auto",
              py: { xs: 2, md: 1 },
            }}
          >
            <motion.div
              ref={trackRef}
              style={{
                x,
                display: "flex",
                alignItems: "stretch",
                gap: "28px",
                width: "max-content",
                paddingLeft: isMobile
                  ? "20px"
                  : "max(48px, calc((100vw - 1500px) / 2))",
                paddingRight: "120px",
                willChange: "transform",
              }}
            >
              {/* --- Lead Editorial Slide 0 --- */}
              <Box
                sx={{
                  width: { xs: "84vw", sm: "380px", md: "420px", lg: "440px" },
                  height: { xs: "58vh", sm: "62vh", md: "66vh" },
                  maxHeight: 640,
                  minHeight: 460,
                  flexShrink: 0,
                  borderRadius: "26px",
                  p: { xs: 3.5, md: 5 },
                  backgroundColor: BROWN.surface,
                  border: `1.5px solid ${BROWN.line}`,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  boxShadow: "0 20px 50px rgba(43, 33, 28, 0.05)",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Decorative background watermark */}
                <Typography
                  sx={{
                    position: "absolute",
                    bottom: { xs: 190, sm: 200 },
                    right: { xs: -230, sm: -280 },
                    fontSize: { xs: "10rem", sm: "12rem" },
                    fontWeight: 900,
                    color: "rgba(160, 111, 80, 0.12)",
                    pointerEvents: "none",
                    lineHeight: 1,
                    userSelect: "none",
                    transform: 'rotate(-90deg)'
                  }}
                >
                  WiYou!
                </Typography>

                <Box>
                  <Typography
                    sx={{
                      fontSize: ".66rem",
                      fontWeight: 800,
                      letterSpacing: ".18em",
                      textTransform: "uppercase",
                      color: BROWN.accent,
                      mb: 2,
                    }}
                  >
                    Engineered Capabilities
                  </Typography>

                  <Typography
                    component="h3"
                    sx={{
                      fontSize: { xs: "1.8rem", md: "2.3rem" },
                      fontWeight: 800,
                      lineHeight: 1.15,
                      letterSpacing: "-.04em",
                      color: BROWN.ink,
                      mb: 2.5,
                    }}
                  >
                    Bespoke solutions crafted without compromise.
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: { xs: ".86rem", md: ".94rem" },
                      lineHeight: 1.7,
                      color: BROWN.muted,
                    }}
                  >
                    Every service is tailored from foundational architecture to
                    pixel-perfect motion, ensuring your brand leads with speed,
                    beauty, and technical authority.
                  </Typography>
                </Box>

                {/* Micro-features pill matrix */}
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1.2,
                      mb: 3.5,
                    }}
                  >
                    {[
                      "Production-Ready",
                      "Sub-Second Performance",
                      "Full Source Access",
                      "Continuous Support",
                    ].map((badge) => (
                      <Box
                        key={badge}
                        sx={{
                          px: 1.6,
                          py: 0.6,
                          borderRadius: "100px",
                          backgroundColor: "rgba(160, 110, 80, 0.08)",
                          border: "1px solid rgba(160, 110, 80, 0.16)",
                          fontSize: ".68rem",
                          fontWeight: 700,
                          color: BROWN.ink,
                          letterSpacing: ".02em",
                        }}
                      >
                        {badge}
                      </Box>
                    ))}
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      color: BROWN.accent,
                      fontSize: ".8rem",
                      fontWeight: 750,
                    }}
                  >
                    <Typography sx={{ fontSize: ".76rem", fontWeight: 750 }}>
                      Scroll down to traverse services
                    </Typography>
                    <motion.div
                      animate={{ x: [0, 8, 0] }}
                      transition={{
                        repeat: Infinity,
                        duration: 1.6,
                        ease: "easeInOut",
                      }}
                    >
                      <ArrowForwardIcon sx={{ fontSize: "1rem" }} />
                    </motion.div>
                  </Box>
                </Box>
              </Box>

              {/* --- Service Cards 1 through N --- */}
              {displayedServices.map((service, index) => {
                const titleText =
                  typeof service.title === "string"
                    ? service.title
                    : typeof service.title?.title === "string"
                    ? service.title.title
                    : "Digital Solution";

                const rawSubtitle =
                  service.subtitle ||
                  service.description ||
                  service.shortDescription;

                const subtitleText =
                  typeof rawSubtitle === "string"
                    ? rawSubtitle
                    : typeof rawSubtitle?.description === "string"
                    ? rawSubtitle.description
                    : typeof rawSubtitle?.text === "string"
                    ? rawSubtitle.text
                    : "Tailored engineering and high-fidelity interface design crafted to solve complex business objectives.";

                const categoryText =
                  typeof service.category === "string"
                    ? service.category
                    : typeof service.category?.title === "string"
                    ? service.category.title
                    : index % 2 === 0
                    ? "Design & Engineering"
                    : "Core Capability";

                // Safely convert tags / features into pure string primitives
                // In Firestore, service.features is often an array of objects { title, description }
                const rawTags = Array.isArray(service.tags)
                  ? service.tags
                  : Array.isArray(service.features)
                  ? service.features
                  : [];

                const stringTags = rawTags
                  .map((item) => {
                    if (typeof item === "string") return item;
                    if (item && typeof item === "object") {
                      return (
                        item.title ||
                        item.name ||
                        item.label ||
                        item.description ||
                        ""
                      );
                    }
                    return "";
                  })
                  .filter((t) => typeof t === "string" && t.trim().length > 0);

                const finalTags =
                  stringTags.length > 0
                    ? stringTags.slice(0, 3)
                    : [
                        "Production Ready",
                        "Custom Architecture",
                        "Responsive Polish",
                      ];

                const isCurrentlyActive = activeServiceIndex === index;

                return (
                  <MotionBox
                    key={service.id || index}
                    whileHover={{ y: -8 }}
                    transition={{
                      duration: 0.45,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    onClick={() => {
                        navigate("/pricing");
                    }}
                    sx={{
                      width: { xs: "84vw", sm: "380px", md: "420px", lg: "440px" },
                      height: { xs: "58vh", sm: "62vh", md: "66vh" },
                      maxHeight: 640,
                      minHeight: 460,
                      flexShrink: 0,
                      borderRadius: "26px",
                      position: "relative",
                      overflow: "hidden",
                      cursor: "pointer",
                      backgroundColor: BROWN.surface,
                      border: `1.5px solid ${
                        isCurrentlyActive
                          ? "rgba(198, 139, 89, 0.45)"
                          : `${BROWN.line}`
                      }`,
                      boxShadow: isCurrentlyActive
                        ? "0 30px 75px rgba(24, 18, 14, 0.28)"
                        : "0 16px 40px rgba(24, 18, 14, 0)",
                      transition:
                        "border-color 0.4s ease, box-shadow 0.4s ease",
                      isolation: "isolate",
                    }}
                  >

                    {/* Top Row: Numerals, Badge & Status */}
                    <Box
                      sx={{
                        position: "relative",
                        zIndex: 3,
                        p: { xs: 3, md: 4 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 1.2,
                          px: 1.8,
                          py: 0.6,
                          borderRadius: "100px",
                          backgroundColor: "rgba(220, 220, 220, 0.26)",
                          backdropFilter: "blur(14px)",
                          border: "0px solid rgba(255, 255, 255, 0.18)",
                          boxShadow: "inset 0 13px 5px rgba(246, 246, 246, 0.83), inset 0 -2px 14px rgba(161, 161, 161, 0.1)",
                        }}
                      >
                        
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            backgroundColor: isCurrentlyActive
                              ? BROWN.accentBright
                              : "rgba(104, 104, 104, 0.7)",
                            boxShadow: isCurrentlyActive
                              ? `0 0 10px ${BROWN.accentBright}`
                              : "none",
                            transition: "all 0.35s ease",
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: ".66rem",
                            fontWeight: 800,
                            letterSpacing: ".12em",
                            textTransform: "uppercase",
                            color: BROWN.accent,
                          }}
                        >
                          {categoryText}
                        </Typography>
                      </Box>

                      {/* Card Number Stamp */}
                      <Typography
                        sx={{
                          fontFamily: "monospace",
                          fontSize: { xs: "1.4rem", md: "1.9rem" },
                          fontWeight: 850,
                          letterSpacing: "-.04em",
                          color: isCurrentlyActive
                            ? BROWN.accent
                            : BROWN.muted,
                          transition: "color 0.4s ease",
                        }}
                      >
                        {String(index + 1).padStart(2, "0")}
                      </Typography>
                    </Box>

                {/* Decorative background watermark */}
                <Typography
                  sx={{
                    position: "absolute",
                    bottom: { xs: 190, sm: 200 },
                    right: { xs: -230, sm: -250 },
                    fontSize: { xs: "10rem", sm: "12rem" },
                    fontWeight: 900,
                    color: "rgba(160, 111, 80, 0.12)",
                    pointerEvents: "none",
                    lineHeight: 1,
                    userSelect: "none",
                    transform: 'rotate(-90deg)'
                  }}
                >
                  {titleText}
                </Typography>

                      <Box
                        sx={{
                          p: { xs: 3, md: 4 },
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.8,
                        }}
                      >
                        <Typography
                          component="h3"
                          sx={{
                            fontSize: {
                              xs: "1.6rem",
                              sm: "1.9rem",
                              md: "2.3rem",
                            },
                            fontWeight: 800,
                            lineHeight: 1.1,
                            letterSpacing: "-.04em",
                            color: BROWN.ink,
                            mb: 1.2,
                          }}
                        >
                          {titleText}
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: { xs: ".82rem", md: ".9rem" },
                            lineHeight: 1.6,
                            color: BROWN.muted,
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {subtitleText}
                        </Typography>
                      </Box>

                    {/* Bottom Content Area */}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 3,
                        p: { xs: 3, md: 4 },
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.8,
                      }}
                    >

                      {/* Feature Tags */}
                      {finalTags && finalTags.length > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.9,
                            pt: 0.5,
                          }}
                        >
                          {finalTags.map((tagText, tagIdx) => (
                            <Box
                              key={tagIdx}
                              sx={{
                                px: 1.3,
                                py: 0.4,
                                borderRadius: "8px",
                                backgroundColor: "rgba(255, 255, 255, 0.08)",
                                backdropFilter: "blur(10px)",
                                border: "1px solid rgba(50, 50, 50, 0.12)",
                                fontSize: ".64rem",
                                fontWeight: 650,
                                color: BROWN.accent,
                                letterSpacing: ".02em",
                              }}
                            >
                              {tagText}
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* Action Bar */}
                      <Box
                        sx={{
                          pt: 1.5,
                          borderTop: "1px solid rgba(255, 255, 255, 0.16)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: ".7rem",
                            fontWeight: 750,
                            letterSpacing: ".12em",
                            textTransform: "uppercase",
                            color: isCurrentlyActive
                              ? BROWN.accentBright
                              : BROWN.muted,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.8,
                            transition: "color 0.3s ease",
                          }}
                        >
                          Explore Details
                        </Typography>

                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: "50%",
                            backgroundColor: isCurrentlyActive
                              ? BROWN.accentBright
                              : "rgba(255, 255, 255, 0.92)",
                            color: isCurrentlyActive ? "#FFFFFF" : BROWN.ink,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: isCurrentlyActive 
                              ? "inset 0 13px 5px rgba(254, 204, 169, 0.33), inset 0 -2px 14px rgba(161, 161, 161, 0.35), 0 4px 14px rgba(131, 131, 131, 0.25)"
                              : "inset 0 13px 5px rgb(255, 255, 255), inset 0 -2px 14px rgba(161, 161, 161, 0.35), 0 4px 14px rgba(131, 131, 131, 0.25)",
                            transition: "all 0.35s ease",
                            "&:hover": {
                              transform: "scale(1.08)",
                            },
                          }}
                        >
                          <NorthEastIcon sx={{ fontSize: "1.1rem" }} />
                        </Box>
                      </Box>
                    </Box>
                  </MotionBox>
                );
              })}

              {/* --- Catalog Finale Slide --- */}
              <Box
                sx={{
                  width: { xs: "84vw", sm: "380px", md: "420px", lg: "440px" },
                  height: { xs: "58vh", sm: "62vh", md: "66vh" },
                  maxHeight: 640,
                  minHeight: 460,
                  flexShrink: 0,
                  borderRadius: "26px",
                  p: { xs: 3.5, md: 5 },
                  backgroundColor: BROWN.ink,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  color: "#FFFFFF",
                  boxShadow: "0 25px 60px rgba(35, 27, 22, 0)",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Box>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.6,
                      py: 0.6,
                      pt: 1,
                      borderRadius: "100px",
                      backgroundColor: "rgba(198, 139, 89, 0.16)",
                      border: "0px solid rgba(198, 139, 89, 0.3)",
                      mb: 2.5,
                    }}
                  >

                    <Typography
                      sx={{
                        fontSize: ".66rem",
                        fontWeight: 800,
                        letterSpacing: ".16em",
                        textTransform: "uppercase",
                        color: BROWN.accentBright,
                      }}
                    >
                      Bespoke Engagement
                    </Typography>
                  </Box>
                    
                {/* Decorative background watermark */}
                <Typography
                  sx={{
                    position: "absolute",
                    bottom: { xs: 190, sm: 200 },
                    right: { xs: -230, sm: -280 },
                    fontSize: { xs: "10rem", sm: "12rem" },
                    fontWeight: 900,
                    color: "rgba(160, 111, 80, 0.12)",
                    pointerEvents: "none",
                    lineHeight: 1,
                    userSelect: "none",
                    transform: 'rotate(-90deg)'
                  }}
                >
                  WiYou!
                </Typography>

                  <Typography
                    component="h3"
                    sx={{
                      fontSize: { xs: "1.8rem", md: "2.3rem" },
                      fontWeight: 800,
                      lineHeight: 1.15,
                      letterSpacing: "-.04em",
                      color: "#FFFFFF",
                      mb: 2,
                    }}
                  >
                    Have a unique technical challenge?
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: { xs: ".86rem", md: ".92rem" },
                      lineHeight: 1.7,
                      color: "rgba(255, 255, 255, 0.78)",
                    }}
                  >
                    We build specialized architectures, private internal tools,
                    and custom web solutions tailored to exact client
                    specifications.
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.8 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate("/custom-quote-request")}
                    endIcon={<NorthEastIcon />}
                    sx={{
                      py: 1.4,
                      borderRadius: "12px",
                      backgroundColor: BROWN.accentBright,
                      color: "#FFFFFF",
                      fontWeight: 750,
                      fontSize: ".86rem",
                      textTransform: "none",
                      boxShadow: "0 6px 20px rgba(198, 139, 89, 0)",
                      "&:hover": {
                        backgroundColor: BROWN.accent,
                        boxShadow: "0 8px 24px rgba(198, 139, 89, 0.5)",
                      },
                    }}
                  >
                    Request custom quote
                  </Button>

                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => navigate("/pricing")}
                    sx={{
                      py: 1.3,
                      borderRadius: "12px",
                      borderColor: "rgba(255, 255, 255, 0.25)",
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: ".84rem",
                      textTransform: "none",
                      "&:hover": {
                        borderColor: "#FFFFFF",
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                      },
                    }}
                  >
                    View full service catalog →
                  </Button>
                </Box>
              </Box>
            </motion.div>
          </Box>

          {/* Bottom Quick-Info Bar */}
          <Box
            sx={{
              maxWidth: 1600,
              width: "100%",
              mx: "auto",
              px: { xs: 3, md: 6 },
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              zIndex: 10,
            }}
          >
            <Typography
              sx={{
                fontSize: ".74rem",
                color: BROWN.muted,
                letterSpacing: ".04em",
                display: { xs: "none", sm: "block" },
              }}
            >
              Swipe or scroll down to advance through capabilities
            </Typography>

          {/* Top HUD: Status Bar & Progress Controls */}
          <Box
            sx={{
              maxWidth: 800,
              width: "100%",
              mx: "auto",
              px: { xs: 3, md: 6 },
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              zIndex: 10,
            }}
          >
            {/* Live Card Counter & Hint */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2,
                  py: 0.8,
                  borderRadius: "100px",
                  backgroundColor: BROWN.surface,
                  border: `0px solid ${BROWN.line}`,
                  boxShadow: "inset 0 13px 5px rgb(255, 255, 255), inset 0 -2px 14px rgba(161, 161, 161, 0.35)",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "monospace",
                    fontSize: ".82rem",
                    fontWeight: 800,
                    letterSpacing: ".06em",
                    color: BROWN.ink,
                  }}
                >
                  {String(activeServiceIndex + 1).padStart(2, "0")}
                </Typography>
                <Box
                  sx={{
                    width: "1px",
                    height: "12px",
                    backgroundColor: BROWN.line,
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "monospace",
                    fontSize: ".82rem",
                    fontWeight: 600,
                    color: BROWN.muted,
                  }}
                >
                  {String(totalCards).padStart(2, "0")}
                </Typography>
              </Box>

              <Typography
                sx={{
                  display: { xs: "none", sm: "block" },
                  fontSize: ".75rem",
                  fontWeight: 700,
                  letterSpacing: ".1em",
                  textTransform: "uppercase",
                  color: BROWN.muted,
                }}
              >
                Services Gallery
              </Typography>
            </Box>
            

            {/* Middle Progress Track Bar */}
            <Box
              sx={{
                flex: { xs: 1, sm: "0 1 260px" },
                mx: { xs: 1, sm: 3 },
                height: "3px",
                borderRadius: "3px",
                backgroundColor: "rgba(160, 110, 80, 0.16)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <motion.div
                style={{
                  height: "100%",
                  backgroundColor: BROWN.accent,
                  borderRadius: "3px",
                  scaleX: smoothProgress,
                  transformOrigin: "left",
                }}
              />
            </Box>

            {/* Quick Navigation Arrows & Direct Catalog Link */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <IconButton
                onClick={() => handleScrollToCard("prev")}
                disabled={activeServiceIndex === 0}
                size="small"
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  border: `0px solid ${BROWN.line}`,
                  backgroundColor: BROWN.surface,
                  color: BROWN.ink,
                  boxShadow: "inset 0 13px 5px rgb(255, 255, 255), inset 0 -2px 14px rgba(161, 161, 161, 0.35)",
                  transition: "all 0.25s ease",
                  "&:hover": {
                    backgroundColor: BROWN.ink,
                    boxShadow: "inset 0 3px 2.5px rgba(255, 255, 255, 0.24), inset 0 1px 2px rgba(118, 117, 117, 0.85)",
                    color: "#FFFFFF",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.35,
                  },
                }}
                aria-label="Previous service"
              >
                <ArrowBackIcon sx={{ fontSize: "1rem" }} />
              </IconButton>

              <IconButton
                onClick={() => handleScrollToCard("next")}
                disabled={activeServiceIndex >= totalCards - 1}
                size="small"
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  border: `0px solid ${BROWN.line}`,
                  backgroundColor: BROWN.surface,
                  color: BROWN.ink,
                  boxShadow: "inset 0 13px 5px rgb(255, 255, 255), inset 0 -2px 14px rgba(161, 161, 161, 0.35)",
                  transition: "all 0.25s ease",
                  "&:hover": {
                    backgroundColor: BROWN.ink,
                    boxShadow: "inset 0 3px 2.5px rgba(255, 255, 255, 0.24), inset 0 1px 2px rgba(118, 117, 117, 0.85)",
                    color: "#FFFFFF",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.35,
                  },
                }}
                aria-label="Next service"
              >
                <ArrowForwardIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Box>
          </Box>

            <Button
              onClick={() => navigate("/pricing")}
              endIcon={<NorthEastIcon sx={{ fontSize: ".9rem" }} />}
              sx={{
                px: 0,
                py: 0,
                color: BROWN.ink,
                fontSize: ".78rem",
                fontWeight: 750,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: BROWN.accent,
                },
              }}
            >
              See all services
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ========================================================================= */}
      {/* 🎨 FEATURED DESIGN PASS / TEMPLATES (Below Sticky Stage)                 */}
      {/* ========================================================================= */}
      <Box
        sx={{
          maxWidth: 1600,
          mx: "auto",
          pt: { xs: 8, md: 12 },
          pb: { xs: 10, md: 14 },
          px: { xs: 3, md: 6 },
          borderTop: `1px solid ${BROWN.line}`,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.1fr .55fr" },
            gap: { xs: 3, md: 8 },
            alignItems: "end",
            mb: { xs: 4, md: 6 },
          }}
        >
          <Box>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1.5,
                mb: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: ".68rem",
                  fontWeight: 800,
                  letterSpacing: ".18em",
                  textTransform: "uppercase",
                  color: BROWN.accent,
                }}
              >
                Featured · Design Pass
              </Typography>
            </Box>

            <Typography
              component="h3"
              sx={{
                fontSize: { xs: "2.2rem", md: "3.4rem" },
                lineHeight: 1,
                letterSpacing: "-.05em",
                fontWeight: 800,
                color: BROWN.ink,
              }}
            >
              Need something
              <br />
              ready to deploy?
            </Typography>
          </Box>

          <Typography
            sx={{
              color: BROWN.muted,
              fontSize: ".92rem",
              lineHeight: 1.7,
              maxWidth: 420,
            }}
          >
            A curated collection of production-ready design assets, templates,
            and QR systems for teams that want to ship without starting from
            zero.
          </Typography>

                          {/* Decorative background watermark */}
                <Typography
                  sx={{
                    position: "absolute",
                    bottom: { xs: 600, sm: 400 },
                    right: { xs: -600, sm: -910 },
                    fontSize: { xs: "8rem", sm: "12rem" },
                    fontWeight: 900,
                    width: "max-content",
                    color: "rgba(160, 111, 80, 0.12)",
                    pointerEvents: "none",
                    lineHeight: 1,
                    userSelect: "none",
                    transform: 'rotate(-90deg)'
                  }}
                >
                  WiYou! Tech Studios
                </Typography>
        </Box>

        {/* Featured Card */}
        <Card
          elevation={0}
          sx={{
            width: "100%",
            minHeight: { xs: "auto", md: 260 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            position: "relative",
            overflow: "hidden",
            borderRadius: "22px",
            backgroundColor: BROWN.surface,
            border: `0px solid ${BROWN.line}`,
            boxShadow: "0 10px 30px rgba(43, 33, 28, 0.04)",
            transition: "transform .3s ease, border-color .3s ease, box-shadow .3s ease",
            "&:hover": {
              transform: "translateY(-4px)",
              borderColor: BROWN.accent,
              boxShadow: "0 20px 45px rgba(43, 33, 28, 0.08)",
            },
          }}
        >
          {/* Image Cover */}
          <Box
            sx={{
              width: { xs: "100%", md: 320 },
              minHeight: { xs: 200, md: 260 },
              flexShrink: 0,
              position: "relative",
              overflow: "hidden",
              backgroundColor: BROWN.soft,
            }}
          >
            <Box
              component="img"
              src="/assets/menu_qr_service-template.png"
              alt="Menu & QR templates"
              sx={{
                width: "100%",
                height: "100%",
                minHeight: { xs: 200, md: 260 },
                objectFit: "cover",
                display: "block",
              }}
            />

            <Box
              sx={{
                position: "absolute",
                zIndex: 2,
                top: 16,
                left: 16,
                px: 1.4,
                py: 0.6,
                borderRadius: "8px",
                backgroundColor: "rgba(252, 250, 246, 0.88)",
                backdropFilter: "blur(14px)",
                border: `1px solid ${BROWN.line}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: ".62rem",
                  fontWeight: 800,
                  letterSpacing: ".14em",
                  color: BROWN.ink,
                }}
              >
                DESIGN PASS
              </Typography>
            </Box>
          </Box>

          {/* Details Content */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              px: { xs: 3, md: 5 },
              py: { xs: 3.5, md: 4 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                mb: 1.2,
              }}
            >
              <Typography
                sx={{
                  fontSize: ".64rem",
                  fontWeight: 800,
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                  color: BROWN.accent,
                }}
              >
                Pass 01 · Featured Suite
              </Typography>

              <Typography
                sx={{
                  fontFamily: "monospace",
                  fontSize: ".68rem",
                  fontWeight: 700,
                  letterSpacing: ".08em",
                  color: BROWN.muted,
                }}
              >
                #TM-001
              </Typography>
            </Box>

            <Typography
              component="h4"
              sx={{
                fontWeight: 800,
                color: BROWN.ink,
                fontSize: { xs: "1.45rem", md: "1.9rem" },
                letterSpacing: "-.035em",
                mb: 1,
              }}
            >
              Menu & QR Interactive Templates
            </Typography>

            <Typography
              sx={{
                color: BROWN.muted,
                lineHeight: 1.65,
                fontSize: ".88rem",
                maxWidth: 620,
              }}
            >
              Complete turnkey menu layouts, branded dynamic QR systems, and
              print-ready vector assets for modern hospitality and contactless
              retail environments.
            </Typography>

            <Box
              sx={{
                mt: 3,
                pt: 2.5,
                borderTop: `1px solid ${BROWN.line}`,
                display: "flex",
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: ".58rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: ".1em",
                      color: "#A08C7C",
                    }}
                  >
                    Format
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: ".75rem",
                      fontWeight: 750,
                      color: BROWN.ink,
                    }}
                  >
                    Digital & Vector
                  </Typography>
                </Box>

                <Box
                  sx={{
                    width: "1px",
                    height: 24,
                    backgroundColor: BROWN.line,
                  }}
                />

                <Box>
                  <Typography
                    sx={{
                      fontSize: ".58rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: ".1em",
                      color: "#A08C7C",
                    }}
                  >
                    Availability
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: ".75rem",
                      fontWeight: 750,
                      color: BROWN.ink,
                    }}
                  >
                    Instant Access
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="contained"
                onClick={() => navigate("/design-templates")}
                endIcon={<NorthEastIcon sx={{ fontSize: "1rem" }} />}
                sx={{
                  alignSelf: { xs: "stretch", sm: "auto" },
                  px: 3,
                  py: 1.1,
                  borderRadius: "10px",
                  fontWeight: 750,
                  fontSize: ".8rem",
                  textTransform: "none",
                  backgroundColor: BROWN.ink,
                  color: BROWN.surface,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: BROWN.accent,
                    boxShadow: "0 6px 20px rgba(160, 110, 80, 0.28)",
                  },
                }}
              >
                Browse template collection
              </Button>
            </Box>
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default ServiceSection;